import {
  FeatureServiceEnvironment,
  FeatureServiceProviderDefinition,
  FeatureServices,
} from '@feature-hub/core';
import { HistoryServiceV1 } from '@feature-hub/history-service';
import {
  ShowroomNavigationManagerV1,
  ShowroomNavigationService,
} from '@volkswagen-onehub/showroom-navigation';
import { CarConfigurationServiceV1 } from '@volkswagen-onehub/car-configuration-feature-service';
import { NavigationTargetV1 } from '@volkswagen-onehub/navigation-service';
import { LocationDescriptorObject, createPath } from 'history';

export interface ShowroomNavigationManagerDependencies extends FeatureServices {
  'car-configuration': CarConfigurationServiceV1;
  's2:history': HistoryServiceV1;
}

export interface ShowroomNavigationManagerConfig {
  readonly currentShowroomNodeId: string | undefined;
}

interface NavigationTargetOptions {
  nodeId?: string;
  showroomNodeId?: string;
  hash?: string;
}

export const defineShowroomNavigationManager = ({
  currentShowroomNodeId,
}: ShowroomNavigationManagerConfig): FeatureServiceProviderDefinition<ShowroomNavigationService> => ({
  id: 'showroom-navigation',

  dependencies: {
    featureServices: {
      'car-configuration': '^1.0.0',
      's2:history': '^1.0.0',
    },
  },

  create(
    env: FeatureServiceEnvironment<ShowroomNavigationManagerDependencies>,
  ): ShowroomNavigationService {
    const historyService = env.featureServices['s2:history'];

    const history =
      typeof window !== 'undefined' && typeof document !== 'undefined'
        ? historyService.createBrowserHistory()
        : historyService.createStaticHistory();

    const createNavigationTarget = ({
      nodeId,
      showroomNodeId,
      hash,
    }: NavigationTargetOptions = {}): NavigationTargetV1 | undefined => {
      const params = new URLSearchParams();

      if (nodeId) {
        params.set('nodeId', nodeId);
      }

      if (showroomNodeId) {
        params.set('showroomNodeId', showroomNodeId);
      }

      const showroomLocation: LocationDescriptorObject = {
        pathname: '/showroom',
        search: `?${params.toString()}`,
        hash,
      };

      return {
        createHref: () => createPath(showroomLocation),

        push: () => {
          history.push(showroomLocation);
        },

        replace: () => {
          history.replace(showroomLocation);
        },
      };
    };

    const featureService: ShowroomNavigationManagerV1 = {
      history,
      showroomNodeId: currentShowroomNodeId,

      createShowroomNavigation: (showroomNodeId) =>
        createNavigationTarget({ showroomNodeId: showroomNodeId as string }),

      createConfigureShowroomNavigation: (showroomNodeId) =>
        createNavigationTarget({
          showroomNodeId: showroomNodeId as string,
          hash: 'trim-selection',
        }),

      createSelectModelInShowroomNavigation: (showroomNodeId, nodeId) =>
        createNavigationTarget({
          showroomNodeId,
          nodeId,
          hash: 'configurator',
        }),
    };

    return {
      '1.0.0': () => ({ featureService }),
      '1.1.0': () => ({ featureService }),
    };
  },
});
